import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, TrendingUp, TrendingDown, Minus, DollarSign, 
  Target, FolderKanban, ChevronRight, ChevronDown, Calendar,
  Layers, ExternalLink, Flag, Link2, Clock, Map, Shield,
  AlertCircle, GitBranch, Users, List, LayoutGrid, Eye, EyeOff,
  Sparkles, ArrowUp, ArrowDown, ArrowRight, Upload
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { api } from '../api/client';
import HierarchicalRoadmap from '../components/HierarchicalRoadmap';
import DetailDrawerV2 from '../components/DetailDrawerV2';
import EnhancedFilterBarV2 from '../components/EnhancedFilterBarV2';
import { useTenant } from '../context/TenantContext';
import { PMOFeatureMilestonesWidget, MilestonesSummary } from '../components/MilestonesWidget';
import { GovernanceMetricsRow } from '../components/GovernanceWidget';
import ScopeCoverageWidget from '../components/ScopeCoverageWidget';
import CockpitQuickActions from '../components/CockpitQuickActions';
import AskAnalyticsButton from '../components/AskAnalyticsButton';
import ExcelUploadModal from '../components/ExcelUploadModal';
import ProjectFavoritesPanel from '../components/ProjectFavoritesPanel';

/**
 * PMO Dashboard V2 - "Decision-Making Cockpit"
 * Enhanced for clarity, usability, visual hierarchy, and actionability
 */
const PMODashboardV2 = () => {
  const navigate = useNavigate();
  const { getCurrency, tenantId } = useTenant();
  
  // Data state
  const [data, setData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [roadmapData, setRoadmapData] = useState([]);
  const [roadmapTimeline, setRoadmapTimeline] = useState(null);  // NEW: Store timeline range
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadTime, setLoadTime] = useState(null);
  
  // UI state
  const [filters, setFilters] = useState({
    portfolio_ids: [],
    program_ids: [],
    project_ids: [],
    pm_ids: [],
    sprint_ids: [],
    release_ids: [],
    statuses: [],
    health_categories: [],
    confidence_min: 0,
    confidence_max: 100,
    date_range: ''
  });
  
  // Section preferences
  const [showHealthyPrograms, setShowHealthyPrograms] = useState(false);
  const [epicGrouping, setEpicGrouping] = useState('project'); // 'project' | 'program' | 'flat'
  const [milestoneView, setMilestoneView] = useState('list'); // 'list' | 'timeline' | 'calendar'
  const [roadmapExpanded, setRoadmapExpanded] = useState(false);
  const [roadmapScale, setRoadmapScale] = useState('month');
  
  // Detail drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('');
  const [drawerData, setDrawerData] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');
  
  // Refresh state
  const [refreshing, setRefreshing] = useState(false);
  
  // Sprint 24+: Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadScope, setUploadScope] = useState({});

  // Performance tracking
  const startLoad = useCallback(() => performance.now(), []);

  useEffect(() => {
    const start = startLoad();
    loadInitialData().finally(() => {
      setLoadTime(((performance.now() - start) / 1000).toFixed(2));
    });
  }, []);

  useEffect(() => {
    if (!loading) {
      loadDashboardData();
    }
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, filtersRes, roadmapRes, releasesRes] = await Promise.all([
        api.getPortfolioManagerDashboard(filters),
        api.getFilters(),
        api.getPMORoadmap({}),  // NEW: Use view-specific PMO roadmap endpoint
        api.getReleases()
      ]);
      
      setData(dashboardRes.data);
      setFilterOptions({
        portfolios: filtersRes.data.portfolios || [],
        programs: filtersRes.data.programs || [],
        projects: filtersRes.data.projects || [],
        project_managers: filtersRes.data.project_managers || [],
        sprints: filtersRes.data.sprints || [],
        releases: releasesRes.data.releases || []
      });
      setRoadmapData(roadmapRes.data?.roadmap || []);
      setRoadmapTimeline(roadmapRes.data?.timeline || null);  // Store timeline range
      setReleases(releasesRes.data?.releases || []);
    } catch (error) {
      console.error('Failed to load PMO dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const portfolioId = filters.portfolio_ids?.[0] || null;
      const programId = filters.program_ids?.[0] || null;
      const projectId = filters.project_ids?.[0] || null;
      
      const [res, roadmapRes] = await Promise.all([
        api.getPortfolioManagerDashboard(filters),
        api.getPMORoadmap({ portfolio_id: portfolioId, program_id: programId, project_id: projectId })  // NEW
      ]);
      setData(res.data);
      setRoadmapData(roadmapRes.data?.roadmap || []);
      setRoadmapTimeline(roadmapRes.data?.timeline || null);  // Update timeline range
    } catch (error) {
      console.error('Failed to load PMO dashboard:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadDashboardData();
    } finally {
      setRefreshing(false);
    }
  };

  const openDrawer = (type, data, title) => {
    // Create standardized inspector context
    const context = {
      entityType: (type || data?.type || 'work-item').toLowerCase(),
      entityId: data?.id,
      entityName: title || data?.name || data?.title,
      dateRange: (data?.planned_start && data?.planned_end) ? {
        start: data.planned_start,
        end: data.planned_end
      } : null,
      parentChain: {
        portfolioId: data?.portfolio_id,
        programId: data?.program_id,
        projectId: data?.project_id,
        epicId: data?.epic_id
      },
      item: data
    };
    setDrawerType(context.entityType);
    setDrawerData(context);
    setDrawerTitle(title);
    setDrawerOpen(true);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    const currency = getCurrency() || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Process data for components
  const kpis = data?.kpis || {};
  
  // Helper function - defined before useMemo that uses it
  const getStatusScore = (status) => {
    const scores = {
      'Blocked': 5, 'Behind': 4, 'At Risk': 3, 'Due Date at Risk': 3,
      'In Progress': 2, 'On Track': 1, 'Completed': 0
    };
    return scores[status] || 1;
  };
  
  const programsAtRisk = useMemo(() => {
    const programs = data?.programs_at_risk || [];
    // Sort by severity: Blocked > Behind > At Risk > Healthy
    const statusOrder = { 'Blocked': 0, 'Behind': 1, 'At Risk': 2, 'In Progress': 3, 'On Track': 4 };
    const sorted = [...programs].sort((a, b) => 
      (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99)
    );
    // Filter healthy if toggle is off
    if (!showHealthyPrograms) {
      return sorted.filter(p => p.status !== 'On Track' && p.status !== 'Completed');
    }
    return sorted;
  }, [data?.programs_at_risk, showHealthyPrograms]);

  const epicsGrouped = useMemo(() => {
    const epics = data?.epic_progress || [];
    
    if (epicGrouping === 'flat') {
      return [{ id: 'all', name: 'All Epics', epics, status: 'Mixed' }];
    }
    
    const groupKey = epicGrouping === 'program' ? 'program_id' : 'project_id';
    const groupNameKey = epicGrouping === 'program' ? 'program_name' : 'project_name';
    
    const groupMap = {};
    epics.forEach(epic => {
      const groupId = epic[groupKey] || 'unknown';
      const groupName = epic[groupNameKey] || 'Unknown';
      
      if (!groupMap[groupId]) {
        groupMap[groupId] = {
          id: groupId,
          name: groupName,
          epics: [],
          blocked_count: 0,
          behind_count: 0,
          worst_status_score: 0
        };
      }
      
      groupMap[groupId].epics.push(epic);
      
      const statusScore = getStatusScore(epic.status);
      if (statusScore > groupMap[groupId].worst_status_score) {
        groupMap[groupId].worst_status_score = statusScore;
        groupMap[groupId].status = epic.status;
      }
      
      if (epic.status === 'Blocked') groupMap[groupId].blocked_count++;
      if (epic.status === 'Behind') groupMap[groupId].behind_count++;
    });
    
    return Object.values(groupMap).sort((a, b) => {
      if (b.worst_status_score !== a.worst_status_score) {
        return b.worst_status_score - a.worst_status_score;
      }
      const aComplete = a.epics.reduce((sum, e) => sum + (e.percent_complete || 0), 0) / a.epics.length;
      const bComplete = b.epics.reduce((sum, e) => sum + (e.percent_complete || 0), 0) / b.epics.length;
      return aComplete - bComplete;
    });
  }, [data?.epic_progress, epicGrouping]);

  const featureMilestones = useMemo(() => {
    const milestones = data?.upcoming_milestones || [];
    const now = new Date();
    
    return milestones.map(m => {
      const dueDate = m.due_date ? new Date(m.due_date) : null;
      let urgency = 'future';
      let daysUntilDue = null;
      
      if (dueDate) {
        daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntilDue < 0) urgency = 'overdue';
        else if (daysUntilDue <= 7) urgency = 'due-7';
        else if (daysUntilDue <= 30) urgency = 'due-30';
      }
      
      return { ...m, urgency, daysUntilDue };
    }).sort((a, b) => {
      // Overdue first, then by days until due
      const urgencyOrder = { 'overdue': 0, 'due-7': 1, 'due-30': 2, 'future': 3 };
      if (urgencyOrder[a.urgency] !== urgencyOrder[b.urgency]) {
        return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
      }
      return (a.daysUntilDue || 999) - (b.daysUntilDue || 999);
    });
  }, [data?.upcoming_milestones]);

  const risks = data?.top_risks || [];
  const issues = data?.top_issues || [];
  const dependencies = data?.blocking_dependencies || [];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
        <p className="text-sm text-[var(--clr-text-muted)]">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--clr-surface-page)]" data-testid="pmo-dashboard-v2">
      {/* Enhanced Filter Bar V2 */}
      <EnhancedFilterBarV2
        filters={filters}
        options={filterOptions}
        onChange={setFilters}
        dashboardType="pmo"
      />

      {/* Dashboard Header with Quick Actions */}
      <div className="px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[var(--clr-text-primary)]">PMO / Portfolio Dashboard</h1>
          <p className="text-sm text-[var(--clr-text-secondary)]">Portfolio-wide visibility and decision support</p>
        </div>
        <div className="flex items-center gap-3">
          <CockpitQuickActions
            contextType={filters.program_ids?.[0] ? 'program' : 'portfolio'}
            contextId={filters.program_ids?.[0] || filters.portfolio_ids?.[0]}
            contextName={
              filters.program_ids?.[0] 
                ? filterOptions.programs?.find(p => p.id === filters.program_ids[0])?.name 
                : filters.portfolio_ids?.[0]
                  ? filterOptions.portfolios?.find(p => p.id === filters.portfolio_ids[0])?.name
                  : null
            }
            onRefresh={handleRefresh}
            refreshing={refreshing}
          />
          
          {/* Sprint 24+: Upload to Project Button */}
          {filters.project_ids?.[0] && (
            <button
              onClick={() => {
                setUploadScope({
                  project_id: filters.project_ids[0],
                  project_name: filterOptions.projects?.find(p => p.id === filters.project_ids[0])?.name,
                  program_id: filters.program_ids?.[0],
                  program_name: filterOptions.programs?.find(p => p.id === filters.program_ids?.[0])?.name,
                  portfolio_id: filters.portfolio_ids?.[0],
                  portfolio_name: filterOptions.portfolios?.find(p => p.id === filters.portfolio_ids?.[0])?.name,
                });
                setShowUploadModal(true);
              }}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
              data-testid="pmo-upload-to-project"
            >
              <Upload className="w-4 h-4" />
              Upload to Project
            </button>
          )}
          
          {/* Sprint 23: Ask Analytics Button for PMO */}
          <AskAnalyticsButton 
            context="pmo_dashboard"
            filters={{
              portfolio_id: filters.portfolio_ids?.[0],
              program_id: filters.program_ids?.[0],
              project_id: filters.project_ids?.[0]
            }}
            variant="pill"
            entryPoint="pmo_dashboard"
          />
        </div>
      </div>

      {/* Sticky KPI Row V2 */}
      <div className="sticky top-[52px] z-20 bg-[var(--clr-surface-card)] border-b border-[var(--clr-border-light)] shadow-[var(--shadow-[var(--shadow-card)])]">
        <div className="px-6 py-3">
          <div className="grid grid-cols-6 gap-3">
            <KPITileV2
              label="Weighted Complete"
              value={`${kpis.percent_complete?.toFixed(1) || 0}%`}
              secondaryValue="+2.3% this month"
              trend={kpis.complete_trend}
              status="healthy"
              icon={<Target className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'completion' }, 'Completion Overview')}
            />
            <KPITileV2
              label="Blocked"
              value={kpis.blocked_count || 0}
              secondaryValue={`${kpis.blocked_pct?.toFixed(1) || 0}% of total`}
              trend={kpis.blocked_trend}
              status="critical"
              icon={<AlertTriangle className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'blocked' }, 'Blocked Work Items')}
            />
            <KPITileV2
              label="Behind"
              value={kpis.behind_count || 0}
              secondaryValue={`${kpis.behind_pct?.toFixed(1) || 0}% of total`}
              trend={kpis.behind_trend}
              status="critical"
              icon={<Clock className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'behind' }, 'Behind Schedule')}
            />
            <KPITileV2
              label="At Risk"
              value={kpis.at_risk_count || 0}
              secondaryValue={`${kpis.at_risk_pct?.toFixed(1) || 0}% of total`}
              trend={kpis.at_risk_trend}
              status="warning"
              icon={<Flag className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'at_risk' }, 'At Risk Items')}
            />
            <KPITileV2
              label="Est. Cost"
              value={formatCurrency(kpis.est_cost)}
              secondaryValue={`Var: ${formatCurrency(kpis.variance)}`}
              status="neutral"
              icon={<DollarSign className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'cost' }, 'Cost Summary')}
            />
            <KPITileV2
              label="Confidence"
              value={`${kpis.delivery_confidence || 0}%`}
              secondaryValue="Schedule reliability"
              trend={kpis.confidence_trend}
              status={kpis.delivery_confidence >= 70 ? 'healthy' : kpis.delivery_confidence >= 50 ? 'warning' : 'critical'}
              icon={<Shield className="w-5 h-5" />}
              onClick={() => openDrawer('kpi', { ...kpis, type: 'confidence' }, 'Delivery Confidence')}
            />
          </div>
        </div>
      </div>

      {/* Main Content - Optimized Spacing */}
      <div className="px-6 py-4 space-y-4">
        {/* Programs at Risk Section */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[var(--clr-sev-blocked)]" />
              Programs at Risk
              <span className="text-xs text-[var(--clr-text-muted)] font-normal">({programsAtRisk.length})</span>
            </h2>
            <button
              onClick={() => setShowHealthyPrograms(!showHealthyPrograms)}
              className="flex items-center gap-1.5 text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-text-secondary)]"
            >
              {showHealthyPrograms ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showHealthyPrograms ? 'Hide Healthy' : 'Show Healthy'}
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
            {programsAtRisk.length > 0 ? programsAtRisk.map(program => (
              <ProgramRiskCardV2
                key={program.id}
                program={program}
                onClick={() => openDrawer('program', program, program.name)}
              />
            )) : (
              <div className="text-[var(--clr-text-muted)] text-sm py-4 px-2">
                {showHealthyPrograms ? 'No programs available' : 'All programs are healthy'}
              </div>
            )}
          </div>
        </section>

        {/* Two Column Layout: Epics + Feature Milestones */}
        <div className="grid grid-cols-2 gap-4">
          {/* Epic Progress by Project/Program */}
          <EpicProgressSection
            groups={epicsGrouped}
            grouping={epicGrouping}
            onGroupingChange={setEpicGrouping}
            onEpicClick={(epic) => openDrawer('epic', epic, epic.name)}
            onGroupClick={(group) => openDrawer(epicGrouping, group, group.name)}
          />

          {/* Feature Milestones - NEW: Shows Feature milestones grouped by Project */}
          <PMOFeatureMilestonesWidget />
        </div>

        {/* Governance Metrics - Filtered by active portfolio/program selection */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4 flex items-center justify-between">
          <GovernanceMetricsRow 
            portfolioId={filters.portfolio_ids?.[0] || null}
            programId={filters.program_ids?.[0] || null}
            projectId={filters.project_ids?.[0] || null}
          />
          <MilestonesSummary level="pmo" />
        </div>

        {/* Scope Coverage - Baseline adoption rates */}
        <ScopeCoverageWidget className="mb-4" />

        {/* Risks & Issues - Three Compact Blocks */}
        <div className="grid grid-cols-3 gap-3">
          <CompactSeverityBlock
            title="High Severity Risks"
            items={risks}
            icon={<AlertTriangle className="w-4 h-4 text-[var(--clr-sev-blocked)]" />}
            colorScheme="red"
            onItemClick={(item) => openDrawer('risk', item, item.name || item.title)}
            onViewAll={() => navigate('/risks')}
          />
          <CompactSeverityBlock
            title="Critical Issues"
            items={issues}
            icon={<AlertCircle className="w-4 h-4 text-orange-500" />}
            colorScheme="orange"
            onItemClick={(item) => openDrawer('issue', item, item.name || item.title)}
            onViewAll={() => navigate('/issues')}
          />
          <CompactSeverityBlock
            title="Dependencies at Risk"
            items={dependencies}
            icon={<Link2 className="w-4 h-4 text-purple-500" />}
            colorScheme="purple"
            onItemClick={(item) => openDrawer('dependency', item, item.name)}
            onViewAll={() => navigate('/dependencies')}
          />
        </div>

        {/* Roadmap Section with Enhanced Controls */}
        <RoadmapSection
          data={roadmapData}
          releases={releases}
          expanded={roadmapExpanded}
          onToggle={() => setRoadmapExpanded(!roadmapExpanded)}
          scale={roadmapScale}
          onScaleChange={setRoadmapScale}
          filters={filters}
          currency={getCurrency()}
          timelineRange={roadmapTimeline}  // Pass timeline range
          onItemClick={(item) => openDrawer(item.type?.toLowerCase() || 'item', item, item.name)}
          onOpenFull={() => navigate('/roadmap')}
        />
      </div>

      {/* Performance indicator */}
      {loadTime && (
        <div className="fixed bottom-4 right-4 text-xs text-[var(--clr-text-muted)] bg-white px-2 py-1 rounded shadow-[var(--shadow-card)]">
          Loaded in {loadTime}s
        </div>
      )}

      {/* Detail Drawer V2 */}
      <DetailDrawerV2
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        type={drawerType}
        context={drawerData}  // Pass as context
        data={drawerData?.item || drawerData}
        title={drawerTitle}
        currency={getCurrency()}
      />
      
      {/* Sprint 24+: Excel Upload Modal */}
      <ExcelUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        scope={uploadScope}
        scopeLocked={Boolean(uploadScope.project_id)}
        onUploadComplete={() => {
          setShowUploadModal(false);
          handleRefresh();
        }}
        title="Upload to Project"
      />
    </div>
  );
};

// ==========================================
// KPI Tile V2 - With trend arrows and color-coded borders - Modern Color System
// ==========================================
const KPITileV2 = ({ label, value, secondaryValue, trend, status, icon, onClick }) => {
  // Status determines the left border color using CSS tokens
  const getStatusBorderColor = (s) => {
    const map = {
      critical: 'var(--clr-sev-blocked)',
      warning: 'var(--clr-sev-atrisk)',
      healthy: 'var(--clr-sev-healthy)',
      neutral: 'var(--clr-sev-notstarted)',
      info: 'var(--clr-sev-inprogress)'
    };
    return map[s] || map.neutral;
  };

  const TrendIcon = trend > 0 ? ArrowUp : trend < 0 ? ArrowDown : ArrowRight;
  // For blocked/behind/at-risk: up is bad, down is good
  const isNegativeMetric = ['Blocked', 'Behind', 'At Risk'].some(s => label.includes(s));
  const getTrendColor = () => {
    if (trend === 0 || trend === undefined || trend === null) return 'text-[var(--clr-text-muted)]';
    if (isNegativeMetric) {
      return trend > 0 ? 'text-[var(--clr-sev-blocked)]' : 'text-[var(--clr-sev-healthy)]';
    }
    return trend > 0 ? 'text-[var(--clr-sev-healthy)]' : 'text-[var(--clr-sev-blocked)]';
  };

  return (
    <button
      onClick={onClick}
      className="p-3 rounded-lg bg-[var(--clr-surface-card)] border-l-4 shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-md)] transition-all text-left group"
      style={{ borderLeftColor: getStatusBorderColor(status) }}
      data-testid={`kpi-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[var(--clr-text-muted)]">{icon}</span>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-0.5 ${getTrendColor()}`}>
            <TrendIcon className="w-3 h-3" />
            <span className="text-xs">{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div className="text-xl font-bold text-[var(--clr-text-primary)] group-hover:opacity-80">{value}</div>
      <div className="text-xs text-[var(--clr-text-muted)] mt-0.5">{label}</div>
      {secondaryValue && <div className="text-xs text-[var(--clr-text-muted)] opacity-70 mt-0.5">{secondaryValue}</div>}
    </button>
  );
};

// ==========================================
// Program Risk Card V2 - With sparkline
// ==========================================
const ProgramRiskCardV2 = ({ program, onClick }) => {
  const statusColors = {
    'Blocked': 'bg-purple-500',
    'Behind': 'bg-[var(--clr-sev-blocked)]',
    'At Risk': 'bg-[var(--clr-sev-atrisk)]',
    'In Progress': 'bg-[var(--clr-brand-primary)] bg-opacity-100',
    'On Track': 'bg-[var(--clr-sev-healthy)]'
  };

  // Generate mock trend data for sparkline (in production, this would come from backend)
  const trendData = useMemo(() => {
    const base = program.percent_complete || 0;
    return [
      { w: 1, v: Math.max(0, base - 15 + Math.random() * 5) },
      { w: 2, v: Math.max(0, base - 10 + Math.random() * 5) },
      { w: 3, v: Math.max(0, base - 5 + Math.random() * 5) },
      { w: 4, v: base }
    ];
  }, [program.percent_complete]);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-56 p-3 bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] hover:border-slate-300 hover:shadow-md transition-all text-left"
      data-testid={`program-risk-${program.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-[var(--clr-text-primary)] truncate pr-2 text-sm">{program.name}</span>
        <span className={`px-1.5 py-0.5 rounded text-xs text-white ${statusColors[program.status] || 'bg-slate-400'}`}>
          {program.status}
        </span>
      </div>
      
      <div className="flex items-end justify-between">
        <div className="space-y-1 text-xs text-[var(--clr-text-muted)]">
          <div className="font-medium text-[var(--clr-text-secondary)]">{program.percent_complete?.toFixed(0) || 0}%</div>
          <div className="flex gap-2">
            {program.risks_count > 0 && <span className="text-[var(--clr-sev-blocked)]">{program.risks_count} risks</span>}
            {program.issues_count > 0 && <span className="text-orange-600">{program.issues_count} issues</span>}
          </div>
          <div>Confidence: {program.confidence || 0}%</div>
        </div>
        
        {/* Sparkline */}
        <div className="w-16 h-8 min-w-[64px] min-h-[32px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <Line 
                type="monotone" 
                dataKey="v" 
                stroke={program.status === 'On Track' ? '#10b981' : program.status === 'At Risk' ? '#f59e0b' : '#ef4444'} 
                strokeWidth={1.5}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </button>
  );
};

// ==========================================
// Epic Progress Section V2
// ==========================================
const EpicProgressSection = ({ groups, grouping, onGroupingChange, onEpicClick, onGroupClick }) => {
  const statusColors = {
    'Blocked': 'bg-purple-500',
    'Behind': 'bg-[var(--clr-sev-blocked)]',
    'At Risk': 'bg-[var(--clr-sev-atrisk)]',
    'In Progress': 'bg-[var(--clr-brand-primary)] bg-opacity-100',
    'Completed': 'bg-[var(--clr-sev-healthy)]'
  };

  const hasEpics = groups.some(g => g.epics?.length > 0);

  return (
    <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-page)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
          <Layers className="w-4 h-4 text-indigo-500" />
          Epic Progress
        </h2>
        <div className="flex items-center gap-1">
          {['project', 'program', 'flat'].map((mode) => (
            <button
              key={mode}
              onClick={() => onGroupingChange(mode)}
              className={`px-2 py-0.5 text-xs rounded ${
                grouping === mode 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]'
              }`}
            >
              {mode === 'flat' ? 'List' : `By ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
            </button>
          ))}
        </div>
      </div>
      
      <div className="max-h-[360px] overflow-y-auto">
        {hasEpics ? groups.map(group => (
          <EpicGroup
            key={group.id}
            group={group}
            statusColors={statusColors}
            onEpicClick={onEpicClick}
            onGroupClick={() => onGroupClick(group)}
            showGroupHeader={grouping !== 'flat'}
          />
        )) : (
          <div className="p-6 text-center">
            <Layers className="w-8 h-8 mx-auto mb-2 text-[var(--clr-text-muted)]" />
            <p className="text-sm text-[var(--clr-text-muted)] mb-1">No Epics available</p>
            <p className="text-xs text-[var(--clr-text-muted)]">Create Epics manually or upload via Excel</p>
          </div>
        )}
      </div>
    </section>
  );
};

const EpicGroup = ({ group, statusColors, onEpicClick, onGroupClick, showGroupHeader }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border-b border-[var(--clr-border-light)] last:border-b-0">
      {showGroupHeader && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-[var(--clr-surface-page)] transition-colors"
        >
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-3.5 h-3.5 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
            <FolderKanban className="w-3.5 h-3.5 text-[var(--clr-text-muted)]" />
            <span className="font-medium text-[var(--clr-text-primary)] text-sm">{group.name}</span>
            <span className={`px-1.5 py-0.5 rounded text-xs text-white ${statusColors[group.status] || 'bg-slate-400'}`}>
              {group.status}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
            <span>{group.epics?.length || 0} epics</span>
            {group.blocked_count > 0 && <span className="text-purple-600">{group.blocked_count} blocked</span>}
          </div>
        </button>
      )}
      
      {(expanded || !showGroupHeader) && (
        <div className={`${showGroupHeader ? 'pl-8' : 'pl-3'} pr-3 pb-2 space-y-0.5`}>
          {group.epics?.map(epic => (
            <button
              key={epic.id}
              onClick={() => onEpicClick(epic)}
              className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-[var(--clr-surface-page)] transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full ${statusColors[epic.status] || 'bg-slate-400'}`} />
                <span className="text-sm text-[var(--clr-text-secondary)] truncate">{epic.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-16 h-1 bg-[var(--clr-border-light)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--clr-brand-primary)] bg-opacity-100 rounded-full" style={{ width: `${epic.percent_complete || 0}%` }} />
                </div>
                <span className="text-xs text-[var(--clr-text-muted)] w-7">{epic.percent_complete?.toFixed(0) || 0}%</span>
                {epic.issues_count > 0 && <span className="text-xs text-orange-600">{epic.issues_count}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// Milestones Section V2 - WHITE CARDS WITH LEFT STRIP
// ==========================================
const MilestonesSection = ({ milestones, viewMode, onViewChange, onMilestoneClick }) => {
  // A/G) Milestones use white background + 3px left strip
  const getUrgencyStyles = (urgency) => ({
    'overdue': { 
      card: 'bg-[var(--clr-surface-card)] border-l-[3px]', 
      borderColor: 'var(--clr-sev-blocked)',
      text: 'text-[var(--clr-text-primary)]', 
      subtext: 'text-[var(--clr-sev-blocked)]',
      dot: 'bg-[var(--clr-sev-blocked)]' 
    },
    'due-7': { 
      card: 'bg-[var(--clr-surface-card)] border-l-[3px]', 
      borderColor: 'var(--clr-sev-atrisk)',
      text: 'text-[var(--clr-text-primary)]', 
      subtext: 'text-[var(--clr-sev-atrisk)]',
      dot: 'bg-[var(--clr-sev-atrisk)]' 
    },
    'due-30': { 
      card: 'bg-[var(--clr-surface-card)] border-l-[3px]', 
      borderColor: 'var(--clr-sev-atrisk)',
      text: 'text-[var(--clr-text-primary)]', 
      subtext: 'text-[var(--clr-text-secondary)]',
      dot: 'bg-[var(--clr-sev-atrisk)]' 
    },
    'future': { 
      card: 'bg-[var(--clr-surface-card)] border-l-[3px]', 
      borderColor: 'var(--clr-sev-healthy)',
      text: 'text-[var(--clr-text-primary)]', 
      subtext: 'text-[var(--clr-text-secondary)]',
      dot: 'bg-[var(--clr-sev-healthy)]' 
    }
  }[urgency] || {
    card: 'bg-[var(--clr-surface-card)] border-l-[3px]',
    borderColor: 'var(--clr-sev-notstarted)',
    text: 'text-[var(--clr-text-primary)]',
    subtext: 'text-[var(--clr-text-secondary)]',
    dot: 'bg-[var(--clr-sev-notstarted)]'
  });

  return (
    <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--clr-border-light)] bg-[var(--clr-surface-subtle)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-[var(--clr-sev-healthy)]" />
          Upcoming Milestones
        </h2>
        <div className="flex items-center gap-1">
          {[
            { id: 'list', icon: List, label: 'List' },
            { id: 'timeline', icon: GitBranch, label: 'Timeline' },
            { id: 'calendar', icon: LayoutGrid, label: 'Calendar' }
          ].map(({ id, icon: Icon, label }) => (
            <button
              key={id}
              onClick={() => onViewChange(id)}
              className={`p-1.5 rounded ${
                viewMode === id ? 'bg-emerald-100 text-emerald-700' : 'text-[var(--clr-text-muted)] hover:bg-[var(--clr-surface-hover)]'
              }`}
              title={label}
            >
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
      </div>
      
      <div className="max-h-[360px] overflow-y-auto p-3">
        {milestones.length === 0 ? (
          <div className="py-6 text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-[var(--clr-text-muted)]" />
            <p className="text-sm text-[var(--clr-text-muted)]">No upcoming milestones</p>
          </div>
        ) : viewMode === 'list' ? (
          <MilestoneListView milestones={milestones} getStyles={getUrgencyStyles} onClick={onMilestoneClick} />
        ) : viewMode === 'timeline' ? (
          <MilestoneTimelineView milestones={milestones} getStyles={getUrgencyStyles} onClick={onMilestoneClick} />
        ) : (
          <MilestoneCalendarView milestones={milestones} getStyles={getUrgencyStyles} onClick={onMilestoneClick} />
        )}
      </div>
    </section>
  );
};

// G) Milestone List View - WHITE CARDS WITH LEFT STRIP
const MilestoneListView = ({ milestones, getStyles, onClick }) => (
  <div className="space-y-2">
    {milestones.slice(0, 12).map((m, idx) => {
      const style = getStyles(m.urgency);
      return (
        <button
          key={m.id || idx}
          onClick={() => onClick(m)}
          className={`w-full flex items-center gap-3 p-2 rounded-lg ${style.card} border border-[var(--clr-border-light)] hover:shadow-[var(--shadow-md)] transition-all text-left`}
          style={{ borderLeftColor: style.borderColor }}
        >
          <span className={`w-2 h-2 rounded-full ${style.dot}`} />
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium ${style.text} truncate`}>{m.name}</div>
            {m.project_name && <div className="text-xs text-[var(--clr-text-muted)] truncate">{m.project_name}</div>}
          </div>
          <div className="text-right flex-shrink-0">
            <div className={`text-sm font-medium ${style.subtext}`}>
              {m.daysUntilDue !== null ? (
                m.daysUntilDue < 0 ? `${Math.abs(m.daysUntilDue)}d late` :
                m.daysUntilDue === 0 ? 'Today' : `${m.daysUntilDue}d`
              ) : '-'}
            </div>
            {m.due_date && <div className="text-xs text-[var(--clr-text-muted)]">{new Date(m.due_date).toLocaleDateString()}</div>}
          </div>
        </button>
      );
    })}
  </div>
);

// G) Milestone Timeline View - WHITE CARDS WITH LEFT STRIP
const MilestoneTimelineView = ({ milestones, getStyles, onClick }) => (
  <div className="relative">
    <div className="absolute top-3 left-3 bottom-3 w-0.5 bg-[var(--clr-border-light)]" />
    <div className="space-y-3 pl-8">
      {milestones.slice(0, 10).map((m, idx) => {
        const style = getStyles(m.urgency);
        return (
          <button
            key={m.id || idx}
            onClick={() => onClick(m)}
            className="w-full text-left relative"
          >
            <div className={`absolute -left-[22px] w-3 h-3 rounded-full ${style.dot} border-2 border-white`} />
            <div 
              className={`p-2 rounded-lg ${style.card} border border-[var(--clr-border-light)] hover:shadow-[var(--shadow-md)] transition-all`}
              style={{ borderLeftColor: style.borderColor }}
            >
              <div className="flex justify-between items-start">
                <div className={`text-sm font-medium ${style.text}`}>{m.name}</div>
                <div className={`text-xs ${style.subtext} font-medium`}>
                  {m.daysUntilDue !== null ? (m.daysUntilDue < 0 ? `${Math.abs(m.daysUntilDue)}d late` : `${m.daysUntilDue}d`) : '-'}
                </div>
              </div>
              {m.project_name && <div className="text-xs text-[var(--clr-text-muted)]">{m.project_name}</div>}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const MilestoneCalendarView = ({ milestones, styles, onClick }) => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const days = Array.from({ length: 35 }, (_, i) => {
    const d = new Date(startOfMonth);
    d.setDate(d.getDate() + i - startOfMonth.getDay());
    return d;
  });

  const milestonesMap = {};
  milestones.forEach(m => {
    if (m.due_date) {
      const key = new Date(m.due_date).toDateString();
      if (!milestonesMap[key]) milestonesMap[key] = [];
      milestonesMap[key].push(m);
    }
  });

  return (
    <div className="grid grid-cols-7 gap-1">
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
        <div key={i} className="text-center text-xs text-[var(--clr-text-muted)] py-1">{d}</div>
      ))}
      {days.map((day, idx) => {
        const dayMilestones = milestonesMap[day.toDateString()] || [];
        const isToday = day.toDateString() === today.toDateString();
        const isCurrentMonth = day.getMonth() === today.getMonth();
        
        return (
          <div
            key={idx}
            className={`aspect-square p-0.5 rounded text-center relative ${
              isCurrentMonth ? 'bg-white' : 'bg-[var(--clr-surface-page)]'
            } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
          >
            <div className={`text-xs ${isCurrentMonth ? 'text-[var(--clr-text-secondary)]' : 'text-[var(--clr-text-muted)]'}`}>
              {day.getDate()}
            </div>
            {dayMilestones.length > 0 && (
              <button
                onClick={() => onClick(dayMilestones[0])}
                className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 flex gap-0.5"
              >
                {dayMilestones.slice(0, 3).map((m, i) => (
                  <span key={i} className={`w-1.5 h-1.5 rounded-full ${styles[m.urgency]?.dot || 'bg-[var(--clr-brand-primary)] bg-opacity-100'}`} />
                ))}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// H) Risk/Issue/Dependency Cards - WHITE CARDS WITH LEFT STRIP
// ==========================================
const CompactSeverityBlock = ({ title, items, icon, colorScheme, onItemClick, onViewAll }) => {
  // H) All severity cards use white bg + colored left strip
  const getSchemeColors = (scheme) => ({
    red: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-blocked)',
      title: 'text-[var(--clr-sev-blocked)]',
      bar: 'bg-[var(--clr-sev-blocked)]',
      chip: 'bg-[var(--clr-sev-blocked)] text-white'
    },
    orange: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-atrisk)',
      title: 'text-[var(--clr-sev-atrisk)]',
      bar: 'bg-[var(--clr-sev-atrisk)]',
      chip: 'bg-[var(--clr-sev-atrisk)] text-white'
    },
    blue: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-sev-inprogress)',
      title: 'text-[var(--clr-sev-inprogress)]',
      bar: 'bg-[var(--clr-sev-inprogress)]',
      chip: 'bg-[var(--clr-sev-inprogress)] text-white'
    },
    purple: { 
      card: 'bg-[var(--clr-surface-card)]',
      borderColor: 'var(--clr-brand-primary)',
      title: 'text-[var(--clr-brand-primary)]',
      bar: 'bg-[var(--clr-brand-primary)]',
      chip: 'bg-[var(--clr-brand-primary)] text-white'
    }
  }[scheme] || {
    card: 'bg-[var(--clr-surface-card)]',
    borderColor: 'var(--clr-sev-notstarted)',
    title: 'text-[var(--clr-text-secondary)]',
    bar: 'bg-[var(--clr-sev-notstarted)]',
    chip: 'bg-[var(--clr-sev-notstarted)] text-white'
  });

  const colors = getSchemeColors(colorScheme);

  // Sort by severity
  const severityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
  const sortedItems = [...items].sort((a, b) => 
    (severityOrder[a.severity] ?? 99) - (severityOrder[b.severity] ?? 99)
  );
  const displayItems = sortedItems.slice(0, 3);

  return (
    <div 
      className={`rounded-lg border border-[var(--clr-border-light)] border-l-[3px] p-3 ${colors.card} shadow-[var(--shadow-card)]`}
      style={{ borderLeftColor: colors.borderColor }}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className={`font-medium flex items-center gap-1.5 text-sm ${colors.title}`}>
          {icon}
          {title}
        </h3>
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${colors.chip}`}>{items.length}</span>
      </div>
      
      <div className="space-y-1.5">
        {displayItems.length > 0 ? displayItems.map((item, idx) => (
          <button
            key={item.id || idx}
            onClick={() => onItemClick(item)}
            className="w-full text-left p-1.5 bg-[var(--clr-surface-subtle)] rounded hover:bg-[var(--clr-surface-hover)] transition-all"
          >
            <div className="flex items-center gap-2">
              <span className={`w-1 h-6 rounded-full ${
                item.severity === 'Critical' ? 'bg-[var(--clr-sev-blocked)]' :
                item.severity === 'High' ? 'bg-[var(--clr-sev-atrisk)]' :
                item.severity === 'Medium' ? 'bg-[var(--clr-sev-atrisk)]' : 'bg-[var(--clr-sev-notstarted)]'
              }`} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[var(--clr-text-primary)] truncate">{item.name || item.title}</div>
                {item.project_name && <div className="text-xs text-[var(--clr-text-muted)] truncate">{item.project_name}</div>}
              </div>
            </div>
          </button>
        )) : (
          <div className="text-xs text-[var(--clr-text-muted)] text-center py-2">None</div>
        )}
      </div>
      
      {items.length > 3 && (
        <button
          onClick={onViewAll}
          className="w-full mt-2 text-xs text-[var(--clr-brand-primary)] hover:opacity-80 flex items-center justify-center gap-1"
        >
          +{items.length - 3} more <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// ==========================================
// Roadmap Section V2
// ==========================================
const RoadmapSection = ({ data, releases, expanded, onToggle, scale, onScaleChange, filters, currency, onItemClick, onOpenFull, timelineRange }) => (
  <section className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full px-3 py-2 flex items-center justify-between bg-[var(--clr-surface-page)] hover:bg-[var(--clr-surface-hover)] transition-colors"
    >
      <h2 className="font-semibold text-[var(--clr-text-primary)] flex items-center gap-2 text-sm">
        <Map className="w-4 h-4 text-blue-500" />
        Portfolio Roadmap
        {Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : v) && (
          <span className="text-xs text-[var(--clr-brand-primary)]">(Filtered)</span>
        )}
      </h2>
      <div className="flex items-center gap-2">
        {expanded && (
          <>
            <select
              value={scale}
              onChange={(e) => onScaleChange(e.target.value)}
              className="text-xs border rounded px-1.5 py-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
            <button
              onClick={(e) => { e.stopPropagation(); onOpenFull(); }}
              className="text-xs text-[var(--clr-brand-primary)] hover:text-blue-700 flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Full View
            </button>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>
    </button>
    
    {expanded && (
      <div className="p-3">
        <HierarchicalRoadmap
          data={data}
          scale={scale}
          showReleaseBands={true}
          releases={releases}
          currency={currency}
          maxDepth={4}  // PMO: Program → Project → Epic → Feature
          timelineRange={timelineRange}  // Pass dynamic timeline
          viewType="pmo"  // Identify view type
          onItemClick={onItemClick}
        />
      </div>
    )}
  </section>
);

export default PMODashboardV2;
